import {useProducts} from './use-products';
import {Button} from '../../ui/buttons/button';
import {Trans} from '../../i18n/trans';
import {Navbar} from '../../ui/navigation/navbar/navbar';
import {Link} from 'react-router-dom';
import {Footer} from '../../ui/footer/footer';
import {Fragment, useState} from 'react';
import {UpsellBillingCycle} from './find-best-price';
import {StaticPageTitle} from '../../seo/static-page-title';
import {PricingTable} from '@common/billing/pricing-table/pricing-table';
import {useAuth} from '@common/auth/use-auth';
import {ForumIcon} from '@common/icons/material/Forum';

export function PricingPage() {
  const query = useProducts('pricingPage');
  const [selectedCycle] = useState<UpsellBillingCycle>('yearly');
  const {user} = useAuth();
  const currentSubscription = user?.subscriptions?.find(sub => sub.valid);
  const currentProduct = query.data?.products.find(
    product => product.id === currentSubscription?.product_id,
  );

  return (
    <Fragment>
      <StaticPageTitle>
        <Trans message="Pricing" />
      </StaticPageTitle>
      <Navbar
        color="bg"
        darkModeColor="transparent"
        border="border-b"
        menuPosition="pricing-table-page"
      />

      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-b from-primary/5 to-transparent">
        <div className="container mx-auto px-24 pb-56 pt-60 text-center">
          <div className="mb-16 inline-flex items-center gap-8 rounded-full border border-primary/20 bg-primary/8 px-16 py-6 text-xs font-semibold uppercase tracking-wider text-primary">
            <span className="h-5 w-5 rounded-full bg-primary" />
            <Trans message="Simple, transparent pricing" />
          </div>
          <h1 className="mb-16 text-4xl font-extrabold tracking-tight md:text-5xl">
            <Trans message="Choose the right plan for you" />
          </h1>
          <p className="mx-auto max-w-xl text-base text-muted md:text-lg">
            <Trans message="All plans include full access to the website builder. Upgrade or downgrade anytime." />
          </p>
          {currentProduct && (
            <div className="mt-24 inline-flex items-center gap-10 rounded-full border border-primary/25 bg-primary/8 px-16 py-8 text-sm font-medium text-primary">
              <span className="h-7 w-7 rounded-full bg-primary/20 text-center text-xs leading-7">✓</span>
              <Trans
                message="You are currently on the :name plan"
                values={{name: <strong>{currentProduct.name}</strong>}}
              />
            </div>
          )}
        </div>
      </div>

      {/* Cards */}
      <div className="container mx-auto px-24 pb-80">
        <PricingTable
          selectedCycle={selectedCycle}
          productLoader="pricingPage"
          className="-mt-8"
        />

        {/* Contact */}
        <div className="mt-80 flex flex-col items-center gap-16 rounded-2xl border bg-alt px-40 py-48 text-center md:flex-row md:text-left">
          <div className="flex h-56 w-56 shrink-0 items-center justify-center rounded-full bg-primary/10">
            <ForumIcon className="text-primary" size="lg" />
          </div>
          <div className="flex-1">
            <p className="text-base font-semibold md:text-lg">
              <Trans message="Have questions about our plans?" />
            </p>
            <p className="mt-4 text-sm text-muted">
              <Trans message="Our support team will be happy to help you choose the right plan." />
            </p>
          </div>
          <Button variant="outline" color="primary" elementType={Link} to="/contact" size="md">
            <Trans message="Contact us" />
          </Button>
        </div>
      </div>

      <Footer className="container mx-auto flex-shrink-0 px-24" />
    </Fragment>
  );
}
